import { changeTypes, eventIconTypes } from '@/common/components/HistoryDrawer/constants'

import { csvImportFieldLabels, inventoryFieldKeys, variantFieldLabels } from './constants'

const isVariantChange = change => change.field_key?.startsWith('inventory.variant.')

const isMarker = change => change.field_key === inventoryFieldKeys.appliedToAllVariants

const getMarkerSuffix = (changes, t) => {
  const hasAllVariantsMarker = changes.some(isMarker)

  if (hasAllVariantsMarker) return ` (${t('aplicado a todas las variantes')})`

  return ''
}

const getVariantDetails = (changes, t) => {
  return changes
    .map(change => variantFieldLabels[change.field_key])
    .filter(Boolean)
    .map(label => ({ label: t(label) }))
}

const formatVariantEvent = (event, t) => {
  const changes = event.changes || []
  const sku = changes.find(c => isVariantChange(c) && !isMarker(c))?.object_name || ''

  return {
    icon: eventIconTypes.edit,
    title: `${t('Variante actualizada')}${getMarkerSuffix(changes, t)}`,
    subtitle: sku || null,
    expandable: true,
    details: getVariantDetails(changes, t),
  }
}

const getProductLocationDetails = (oldValue, t) => {
  const details = []

  if (oldValue?.details) {
    details.push({ label: t('Detalles'), value: String(oldValue.details) })
  }

  return details
}

const formatProductLocationEvent = (change, t) => {
  const isCreated = change.change_type === changeTypes.objectCreated
  const locationName = change.object_name || ''
  const sku = change.object_internal_id || ''
  const subtitleParts = [locationName, sku].filter(Boolean)
  const sourceValue = isCreated ? change.new_value : change.old_value
  const details = getProductLocationDetails(sourceValue, t)

  return {
    icon: isCreated ? eventIconTypes.plus : eventIconTypes.trash,
    title: isCreated ? t('Ubicación de producto creada') : t('Ubicación de producto borrada'),
    subtitle: subtitleParts.length > 0 ? subtitleParts.join(' — ') : null,
    expandable: details.length > 0,
    details,
  }
}

const formatPrice = price => {
  if (!price || price.amount === undefined || price.amount === null) return null

  const currency = price.currency ? ` ${price.currency}` : ''

  return `${price.amount}${currency}`
}

const getExportDetailsDetails = (oldValue, t) => {
  const details = []
  const formattedPrice = formatPrice(oldValue?.price)

  if (formattedPrice) {
    details.push({ label: t('Precio'), value: formattedPrice })
  }

  if (oldValue?.customs_code) {
    details.push({ label: t('Código de aduanas'), value: String(oldValue.customs_code) })
  }

  if (oldValue?.mid_code) {
    details.push({ label: 'MID code', value: String(oldValue.mid_code) })
  }

  return details
}

const formatExportDetailsEvent = (change, t) => {
  const isCreated = change.change_type === changeTypes.objectCreated
  const countryName = change.object_name || ''
  const sku = change.object_internal_id || ''
  const subtitleParts = [countryName, sku].filter(Boolean)
  const sourceValue = isCreated ? change.new_value : change.old_value
  const details = getExportDetailsDetails(sourceValue, t)

  return {
    icon: isCreated ? eventIconTypes.plus : eventIconTypes.trash,
    title: isCreated ? t('Información de exportación creada') : t('Información de exportación borrada'),
    subtitle: subtitleParts.length > 0 ? subtitleParts.join(' — ') : null,
    expandable: details.length > 0,
    details,
  }
}

const formatCsvImportEvent = (change, t) => {
  const values = change.new_value || {}
  const details = Object.entries(csvImportFieldLabels)
    .filter(([key]) => values[key] !== undefined && values[key] !== null)
    .map(([key, label]) => ({ label: t(label), value: String(values[key]) }))

  return {
    icon: eventIconTypes.upload,
    title: t('CSV importado'),
    subtitle: null,
    expandable: details.length > 0,
    details,
  }
}

const formatCsvExportEvent = t => ({
  icon: eventIconTypes.download,
  title: t('CSV exportado'),
  subtitle: null,
  expandable: false,
  details: [],
})

export const formatInventoryEvent = (event, t) => {
  const changes = event.changes || []
  const firstChange = changes[0]

  if (firstChange?.field_key === inventoryFieldKeys.csvImport) return formatCsvImportEvent(firstChange, t)
  if (firstChange?.field_key === inventoryFieldKeys.csvExport) return formatCsvExportEvent(t)

  if (changes.some(isVariantChange)) return formatVariantEvent(event, t)

  if (firstChange?.field_key === inventoryFieldKeys.productLocation) return formatProductLocationEvent(firstChange, t)
  if (firstChange?.field_key === inventoryFieldKeys.exportDetails) return formatExportDetailsEvent(firstChange, t)

  return formatVariantEvent(event, t)
}
